import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { useMemedPrescription, extractMemedPrescriptionData, extractMemedPrescriptionId } from '@/hooks/useMemedPrescription';
import { memedApi } from '@/api';

export function HospitalGatewayPage() {
  const [searchParams] = useSearchParams();
  const numAtendimento = searchParams.get('num_atendimento') || searchParams.get('atendimento') || 'ATD-1001';
  const numProntuario = searchParams.get('num_prontuario') || searchParams.get('prontuario') || 'PEP-98765';
  const crmUsuario = searchParams.get('usuario') || searchParams.get('crm') || 'CRM/SP';
  const pacienteNome = searchParams.get('paciente_nome') || 'Paciente Hospitalar';
  const pacienteCpf = searchParams.get('paciente_cpf') || '000.000.000-00';

  const [completedPrescription, setCompletedPrescription] = useState<any | null>(null);

  const { openPrescription, onMemedEvent, loadScript } = useMemedPrescription();

  // Listener para capturar a emissão oficial e devolver ao sistema hospitalar via postMessage
  useEffect(() => {
    const unsubscribe = onMemedEvent('prescricaoImpressa', (data: any) => {
      const memedId = extractMemedPrescriptionId(data);
      const extracted = extractMemedPrescriptionData(data);

      setCompletedPrescription({
        memedId,
        pdfUrl: data?.pdfUrl,
        medicamentos: extracted.medications,
      });

      // Devolve para a janela pai do WebView (MV Soul ou Philips Tasy)
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            {
              type: 'MEDIKEI_MEMED_PRESCRIPTION_SAVED',
              numAtendimento,
              numProntuario,
              prescriptionId: memedId,
              pdfUrl: data?.pdfUrl,
              signed: true,
            },
            '*',
          );
        }
      } catch (err) {
        console.warn('[HospitalGateway] Falha ao enviar postMessage para a janela pai:', err);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [onMemedEvent, numAtendimento, numProntuario]);

  const handleLaunchMemed = useCallback(async () => {
    try {
      const tokenRes = await memedApi.getToken().catch(() => ({ token: undefined }));
      await loadScript(tokenRes?.token);

      const memedPatient = {
        idExterno: numProntuario,
        nome: pacienteNome,
        cpf: pacienteCpf.replace(/\D/g, ''),
        sexo: 'Feminino',
      };

      const workplace = {
        cnes: '0000000',
        local_name: 'Medikei Hospital Gateway (Tasy / MV Soul)',
        city: 'São Paulo',
        state: 'SP',
      };

      await openPrescription(
        memedPatient as any,
        workplace as any,
        [],
        [],
        { appointmentId: numAtendimento },
        {
          token: tokenRes?.token,
          featureToggles: {
            forceSign: false,
            buttonClose: true,
            deletePatient: false,
          },
          additionalData: {
            header: [
              { Atendimento: `Atendimento Hospitalar nº ${numAtendimento}` },
              { Prontuário: `PEP nº ${numProntuario}` },
              { Sistema: 'Barramento Medikei B2B (MV / Tasy)' },
            ],
          },
        },
      );
    } catch (err) {
      console.error('[HospitalGateway] Erro ao abrir Memed:', err);
    }
  }, [numAtendimento, numProntuario, pacienteNome, pacienteCpf, loadScript, openPrescription]);

  useEffect(() => {
    handleLaunchMemed();
  }, [handleLaunchMemed]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col justify-between">
      {/* Barra de Topo Hospitalar */}
      <header className="h-14 border-b border-border bg-card px-4 flex items-center justify-between text-xs sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-4 w-4" />
          </div>
          <div>
            <strong className="text-foreground block font-bold">
              Barramento Hospitalar Medikei B2B &bull; MV Soul & Philips Tasy
            </strong>
            <span className="text-muted-foreground text-[11px]">
              Atendimento: <strong className="text-foreground">{numAtendimento}</strong> &bull; PEP:{' '}
              <strong className="text-foreground">{numProntuario}</strong> &bull; Médico: {crmUsuario}
            </span>
          </div>
        </div>

        <Badge variant="outline" className="font-semibold text-emerald-600 bg-emerald-500/10 border-emerald-500/20">
          <ShieldCheck className="w-3 h-3 mr-1" /> Assinatura ICP-Brasil Ativa
        </Badge>
      </header>

      {/* Conteúdo Central */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        {completedPrescription ? (
          <div className="max-w-md w-full p-8 rounded-2xl border border-emerald-500/30 bg-card shadow-xl text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Prescrição Registrada com Sucesso!</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Os dados assinados com ICP-Brasil foram integrados ao prontuário hospitalar.
              </p>
            </div>

            {completedPrescription.pdfUrl && (
              <Button
                onClick={() => window.open(completedPrescription.pdfUrl, '_blank')}
                className="w-full h-11 rounded-xl font-bold gap-2 cursor-pointer"
              >
                <span>Visualizar Documento Oficial</span>
              </Button>
            )}
          </div>
        ) : (
          <div className="text-center space-y-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            <p className="text-sm font-semibold text-foreground">Conectando ao terminal de prescrição eletrônica...</p>
            <p className="text-xs text-muted-foreground">
              Carregando certificado digital e dados do paciente para o atendimento hospitalar.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
